import { Megaphone } from 'lucide-react';
import { Dialog } from '../ui/Dialog/Dialog';
import { Badge } from '../atoms/Badge/Badge';
import { Text } from '../atoms/Typography/Text';
import { Stack } from '../atoms/Stack/Stack';
import { Inline } from '../atoms/Inline/Inline';
import { EmptyState } from '../atoms/EmptyState';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { ReleaseNotePreview } from '../admin/ReleaseNotePreview';
import { formatDate } from '../admin/adminFormat';
import { useTranslation } from '../../i18n/useTranslation';
import { newsLanguage, type InAppNewsNote } from '../../services/newsService';
import './NewsButton.css';

export interface NewsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  notes: InAppNewsNote[];
}

/** The "Neuigkeiten" dialog: release notes shown in the app, newest first. */
export function NewsDialog({ isOpen, onClose, isLoading, isError, onRetry, notes }: NewsDialogProps) {
  const { t, i18n } = useTranslation();
  const lang = newsLanguage(i18n.resolvedLanguage ?? i18n.language ?? 'en');

  let content;
  if (isLoading) {
    content = (
      <LiveRegion busy>
        <Text>{t('news.loading')}</Text>
      </LiveRegion>
    );
  } else if (isError) {
    content = <LoadErrorState titleLevel={3} title={t('news.loadError')} onRetry={onRetry} />;
  } else if (notes.length === 0) {
    content = (
      <EmptyState
        icon={<Megaphone size={32} aria-hidden="true" />}
        headline={t('news.emptyTitle')}
        description={t('news.emptyText')}
        headingLevel={3}
      />
    );
  } else {
    content = (
      <ol className="news-dialog__list">
        {notes.map((note) => (
          <li key={note.id} className="news-dialog__item">
            <Stack gap="2">
              <Inline gap="2" align="center" wrap>
                {note.isNew && <Badge tone="info">{t('news.newBadge')}</Badge>}
                <Text as="span" size="sm" color="muted">
                  {note.appVersion ? `${t('appVersion.label', { version: note.appVersion })} · ` : ''}
                  {formatDate(note.date, i18n.language)}
                </Text>
              </Inline>
              <ReleaseNotePreview
                title={lang === 'de' ? note.titleDe : note.titleEn}
                body={lang === 'de' ? note.bodyDe : note.bodyEn}
                titleLevel={3}
                lang={lang}
              />
            </Stack>
          </li>
        ))}
      </ol>
    );
  }

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title={t('news.dialogTitle')} size="lg" data-testid="news-dialog">
      {content}
    </Dialog>
  );
}
